<script setup lang="ts">
import { JobOccurrenceDto, ReportOptionDto, WorkItemDto } from '@/api/generator';
import unicheckSoftwareLogo from '@/assets/images/unicheck-sw-logo.svg';
import TimeStamp from '@/components/EntityHistory/components/TimeStamp.vue';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import { getDateTimeFormatted } from '@/components/formatters/utils.ts';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { truthy } from '@/utils/helper.ts';
import { I18nKey } from '@/utils/typings.ts';
import JobExecutionProgress from '@/views/jobOccurrences/components/JobExecutionProgress.vue';
import { summarizeWorkItemProgress } from '@/views/jobOccurrences/utils/jobExecutionProgress';
import { camelCase } from 'lodash';
import { DateTime } from 'luxon';
import { comments } from '@/components/icons/icons';
import { computed } from 'vue';
import { NIL } from 'uuid';
import { formatPlainTextAddressToHtml } from '@/utils/stringUtils';
import { getImageUrl } from '@/components/images/utils';
import FileListItem from '@/components/forms/Attachments/components/FileListItem.vue';
import Sanitize from '@/components/Sanitize/Sanitize.vue';

interface JobOccurrencesReportViewProps {
  data: JobOccurrenceDto;
  user: string;
  protocols: string[];
  ipin: string;
  reportOption?: ReportOptionDto;
}

const props = defineProps<JobOccurrencesReportViewProps>();

const JOB_REPORT_CLIENT_LOGO_WIDTH = 189;

const time = DateTime.now().toISOTime();
const reportDateString = `${getDateTimeFormatted({ format: 'short', value: time })} | ${props.user}`;

const formattedAddress = computed(() =>
  formatPlainTextAddressToHtml(props.reportOption?.address ?? ''),
);

function getUserNames(workItem: WorkItemDto) {
  return [
    ...new Set(
      workItem.workSteps
        ?.flatMap((step) => step.lastModifierUser?.fullName ?? step.creatorUser?.fullName)
        .filter(truthy) ?? [],
    ),
  ];
}
</script>

<template>
  <div class="report-content">
    <section class="job-data">
      <div class="document-header">
        <div class="left">
          <div class="logo">
            <img width="auto" height="20" alt="PTW Logo" :src="unicheckSoftwareLogo" />
          </div>
          <h1 class="report-name text-h4" :data-report-name="$t('JobReport')">
            {{ $t('JobReport') }}
          </h1>
          <IdentifierLabel
            :label="data.jobConfigurationName"
            :identifier="data.jobConfigurationIuid"
          />
          <div
            class="job-details"
            :data-report-type="`${$t('ReportType')}: ${$t('JobResultsOverview')}`"
            :data-report-date="reportDateString"
          >
            <v-table density="compact" class="table-no-borders">
              <tr>
                <th scope="row">{{ $t('MedicalSystem_one') }}:</th>
                <td>
                  <IdentifierLabel
                    :label="data.targetSystem?.name"
                    :identifier="data.targetSystem?.iuid"
                  />
                </td>
              </tr>
              <tr>
                <th scope="row">{{ $t('Protocol_other') }}:</th>
                <td>
                  {{ protocols.length ? protocols.join(', ') : EMPTY_FIELD_SIGN }}
                </td>
              </tr>
              <tr>
                <th scope="row">{{ $t('StateOfExecution') }}:</th>
                <td>
                  {{ $t(`StateOfUseEnum.${data.state}` as I18nKey) }} (<DateTimeFormatter
                    :value="data.executionCompleted"
                    class="timeInfo"
                  />
                  {{ $t('By') }} {{ user }})
                </td>
              </tr>
            </v-table>
          </div>
        </div>
        <div class="right">
          <div class="client-information" :data-ipin="ipin">
            <div v-if="reportOption?.imageId && reportOption?.imageId !== NIL" class="client-logo">
              <img
                :src="getImageUrl(reportOption.imageId, { width: JOB_REPORT_CLIENT_LOGO_WIDTH })"
                alt="Client Logo"
              />
            </div>
            <div class="h-50">
              <Sanitize :value="formattedAddress" :options="{ ALLOWED_TAGS: ['br', 'strong'] }" />
            </div>
          </div>
        </div>
      </div>
      <VDivider class="my-2" :thickness="1" color="black" />
    </section>

    <section v-if="data.comments?.length" class="job-comments">
      <h6>{{ $t('JobComment_other') }}</h6>

      <div v-for="comment in data.comments" :key="comment.id" class="comment d-flex ga-2 mb-2">
        <v-icon>{{ comments }}</v-icon>

        <div class="d-flex flex-column ga-2">
          <TimeStamp
            class="report-date text-caption"
            singleLine
            :time="comment.lastModificationTime ?? comment.creationTime"
            :user="comment.lastModifierUser?.fullName ?? comment.creatorUser?.fullName"
          />
          <p>{{ comment.description }}</p>

          <v-list v-if="comment?.attachments?.length" class="filelist">
            <FileListItem
              v-for="file in comment.attachments"
              :key="file.name"
              :file="file"
              mode="details"
            />
          </v-list>
        </div>
      </div>
    </section>

    <section class="work-items-section" :class="!data.comments?.length ? 'mt-0' : ''">
      <div v-if="data.workProgressSummary" class="d-flex align-center mt-2">
        <span class="text-h6"> {{ $t('ResultsSummary') }}: </span>
        <JobExecutionProgress :value="data.workProgressSummary" />
      </div>

      <VDivider class="my-2" :thickness="1" color="black" />
      <table class="w-100">
        <thead>
          <tr>
            <th scope="col">{{ $t('WorkItem_one') }}</th>
            <th scope="col">{{ $t('Result_one') }}</th>
            <th scope="col">{{ $t('User.User') }}</th>
            <th scope="col">{{ $t('Comment_other') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in data.workItems" :key="item.id!">
            <td class="text-no-wrap">
              <IdentifierLabel
                :label="item.name"
                :identifier="item.iuid"
                :subline="$t(`TaskTypeDescriptionEnum.${item.taskType}` as I18nKey)"
              />
            </td>
            <td>
              <JobExecutionProgress
                :value="summarizeWorkItemProgress(item as WorkItemDto)"
                class="mr-3"
              />
            </td>
            <td>
              <ul class="user-list">
                <li
                  v-for="userName in getUserNames(item as WorkItemDto)"
                  :key="`${camelCase(userName)}`"
                >
                  {{ userName }}
                </li>
              </ul>
            </td>
            <td>
              <div class="comment d-flex mb-2 flex-column">
                <div
                  v-for="comment in item.comments"
                  :key="comment.id"
                  class="comment d-flex ga-2 mb-2 flex-column"
                >
                  <div class="d-flex flex-colum w-100 ga-2">
                    <v-icon>{{ comments }}</v-icon>
                    <div>
                      <TimeStamp
                        class="report-date text-caption mb-1"
                        singleLine
                        :time="comment.creationTime"
                        :user="user"
                      />
                      <p class="w-100">{{ comment.description }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <section class="signature mt-6">
      <h6>{{ $t('Signature') }}</h6>
      <div class="signature-content w-33">
        <VDivider class="mt-12 mb-2" :thickness="1" color="black" />
        <div class="signee">
          {{ $t('Date') }} /
          {{ $t('Roles.Predefined.NamePtw100MedicalPhysicist') }}
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
:deep(.v-list) {
  background: white;
}
:deep(.v-list-item) {
  cursor: default !important;
  pointer-events: auto;
  .v-list-item__overlay {
    display: none;
  }
}

@media screen, pagedjs-ignore {
  .report-content {
    max-width: 210mm;
    margin: 0 auto;
  }
}

.logo {
  display: flex;
  align-items: center;

  img {
    width: auto;
    height: 0.7cm;
  }
}

.client-logo {
  width: 5cm;
  object-position: left bottom;
  object-fit: contain;

  img {
    width: auto;
    max-height: 2cm;
  }
}

.document-header {
  margin-top: 10mm;

  display: grid;
  grid-template-areas: 'left right';
  grid-template-columns: auto 5cm;
  grid-column-gap: 3mm;

  .client-information {
    font-size: 9pt;
    position: relative;
    top: -1.76cm;

    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 0.1cm;
  }

  .job-details {
    margin-top: 2rem;
    margin-bottom: 1rem;

    :deep(.v-table) {
      > .v-table__wrapper > table {
        width: unset;
      }
    }
  }
}

@media print {
  .report-content {
    margin: 0;
    padding: 0;
  }
}
</style>
